<template>
  <StepLayout
    :back="back"
    :next="next"
    helpscoutArticle="665890dff0fdf93e4cf09b70"
    :isEditStepRoute="isEditStepRoute"
    buttonDataEc="envelopes_prefill_fields_page"
  >
    <template slot="title">
      {{ $t('ui.envelopes.create.steps.fields.title') }}
    </template>

    <template v-if="envelopeHasPrefillFields">
      <p class="mb-lg">
        {{ $t('ui.envelopes.create.steps.fields.description') }}
      </p>

      <HeaderContainer class="mb-lg shadow-box">
        <template v-slot:header>
          <p class="font-bold my-base md:hidden">
            {{ $t('ui.envelopes.create.steps.fields.prefillableFields') }}
          </p>
          <div class="py-base items-center hidden md:grid md:grid-cols-2">
            <p class="font-bold mb-0">
              {{ $t('ui.envelopes.create.steps.fields.prefillableFieldName') }}
            </p>
            <p class="font-bold mb-0">
              {{ $t('ui.envelopes.create.steps.fields.dataToBeAddedToTheseFields') }}
            </p>
          </div>
        </template>

        <template v-slot:default>
          <div class="py-sm px-base md:py-md md:px-lg">
            <div
              v-for="field in mergeFields"
              :key="field.name"
              class="md:grid md:grid-cols-2 items-center mb-base md:mb-md"
            >
              <div class="mb-xs md:mb-0 min-w-0">
                <p class="md:hidden mb-0">
                  {{  $t('ui.envelopes.create.steps.fields.prefillableFieldName') }}
                </p>
                <p class="font-bold md:font-normal">{{ field.name }}</p>
              </div>
              <FloatingLabelTextArea
                class="!min-w-0 mb-0"
                v-model="field.value"
                :placeholder="$t('ui.envelopes.create.steps.fields.addFieldDataHere')"
                :ariaLabel="$t('ui.envelopes.create.steps.fields.valueFor', { field: field.name })"
              />
            </div>
          </div>
        </template>
      </HeaderContainer>
    </template>
    <p
      class="mb-lg"
      v-else
    >
      {{ $t('ui.envelopes.create.steps.fields.descriptionNoFields') }}
    </p>

    <OutdatedDraftModal
      :updateError="updateErrorEnvelopeFields"
      @close="resetUpdateErrorEnvelopeFields"
    />
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed, defineProps, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';

import {
  useEnvelope,
  useEnvelopesRoutes,
  useEnvelopesStepsNavigation,
  useUpdateEnvelopeFields,
} from '@/composables/envelopes';
import {
  useUpdateWithValidation,
  useValidation,
} from '@/composables/envelopes/validation';
import { EnvelopeFieldsUpdate } from '@/store/modules/envelopes/models/EnvelopeFields';

import StepLayout from '@/components/layouts/StepLayout';
import HeaderContainer from '@/components/library/containers/HeaderContainer/HeaderContainer.vue';
import { OutdatedDraftModal } from '@/components/ui/envelopeCreate/OutdatedDraftModal';
import FloatingLabelTextArea from '@/components/tailwind/forms/FloatingLabelTextarea.vue';

/** @typedef {import('@/store/modules/envelopes/models/EnvelopeFields').TEnvelopeFields} TEnvelopeFields */

const props = defineProps({
  /** @type {import('vue').PropOptions<TEnvelopeFields>} */
  envelopePartyFields: {
    type: Array,
    required: true,
  },
});

/** @type {import('vue').Ref<{name:string,value:string|null}[]>} */
const mergeFields = ref([]);

const getMergeFields = () => {
  const fields = props.envelopePartyFields
    ?.flatMap(party =>
      party.documents.flatMap(document => document.fields))
    .sort(a => (a.merge_field ? 1 : -1))
    .filter(field => field.merge_field);

  const deduplicated = [...new Map(fields?.map(field => [field.merge_field, field.value]))];

  return deduplicated.map(([name, value]) => ({
    name,
    value,
  }));
};

watch(
  () => props.envelopePartyFields,
  () => { mergeFields.value = getMergeFields(); },
  { immediate: true },
);

const route = useRoute();
const { isEditStepRoute } = useEnvelopesRoutes();

const { envelope } = useEnvelope(route.params.fingerprint);

const envelopeFieldsUpdate = computed(() => {
  const mergeFieldsMap = new Map(mergeFields.value.map(field => [field.name, field.value]));

  return {
    data:
      props.envelopePartyFields?.flatMap(party =>
        party.documents.flatMap(document =>
          document.fields.map(field => ({
            input_id: field.input_id,
            value: mergeFieldsMap.get(field.merge_field) || null,
          })))) || [],
    envelope_progressed_at: envelope.value?.steps_progress?.updated_at || null,
  };
});

const envelopeHasPrefillFields = computed(() => envelopeFieldsUpdate.value.data?.length);

const { updateEnvelopeFields, updateErrorEnvelopeFields, resetUpdateErrorEnvelopeFields } = useUpdateEnvelopeFields(route.params.fingerprint);

const {
  isValid, data, validate,
} = useValidation(
  envelopeFieldsUpdate,
  EnvelopeFieldsUpdate,
);

const updateWithValidation = useUpdateWithValidation(updateEnvelopeFields, data, validate);

function update() {
  return envelopeHasPrefillFields.value
    ? updateWithValidation()
    : Promise.resolve({});
}

const { back, next } = useEnvelopesStepsNavigation(update, isValid);
</script>
